import { formatDate as formatWithSettings } from '../../utils/dateFormat';

const riskOrder = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1
};

export const normalizeSeverity = (severity) => {
  const value = (severity || '').toLowerCase();
  if (['critical', 'high', 'medium', 'low'].includes(value)) return value;
  if (['moderate', 'mod', 'mid'].includes(value)) return 'medium';
  if (['severe'].includes(value)) return 'high';
  if (['minor', 'info'].includes(value)) return 'low';
  return 'low';
};

export const getRiskLabel = (severity) => {
  const normalized = normalizeSeverity(severity);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const getRiskVariant = (severity) => normalizeSeverity(severity);

export const getRiskColor = (severity) => {
  const normalized = normalizeSeverity(severity);
  if (normalized === 'critical') return 'var(--sp-red)';
  if (normalized === 'high') return 'var(--sp-orange)';
  if (normalized === 'medium') return 'var(--sp-yellow)';
  return 'var(--sp-border)';
};

export const formatDate = (value) => formatWithSettings(value);

export const isActiveStatus = (status) => {
  const value = (status || '').toLowerCase();
  return value === 'open' || value === 'monitoring';
};

export const isClosedStatus = (status) => {
  const value = (status || '').toLowerCase();
  return value === 'closed' || value === 'resolved';
};

export const isOverdueReview = (event, today = new Date()) => {
  if (!event?.reviewDate) return false;
  const reviewDate = new Date(event.reviewDate);
  if (Number.isNaN(reviewDate.getTime())) return false;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return reviewDate < startOfToday && isActiveStatus(event.status);
};

export const splitActiveHistorical = (events = []) => {
  return events.reduce(
    (acc, event) => {
      if (isActiveStatus(event.status)) {
        acc.active.push(event);
      } else {
        acc.historical.push(event);
      }
      return acc;
    },
    { active: [], historical: [] }
  );
};

export const sortByRiskAndDate = (events = []) => {
  return [...events].sort((a, b) => {
    const riskDelta = riskOrder[normalizeSeverity(b.severity)] - riskOrder[normalizeSeverity(a.severity)];
    if (riskDelta !== 0) return riskDelta;
    const dateA = new Date(a.date || 0).getTime();
    const dateB = new Date(b.date || 0).getTime();
    return dateB - dateA;
  });
};

export const isActionRequired = (event, today = new Date()) => {
  const severity = normalizeSeverity(event?.severity);
  if (severity !== 'critical' && severity !== 'high') return false;
  const missingOwner = !event?.assignedOwner;
  const missingReview = !event?.reviewDate;
  const overdue = isOverdueReview(event, today);
  return isActiveStatus(event?.status) || missingOwner || missingReview || overdue;
};

export const getActionRequiredEvents = (events = [], today = new Date()) => {
  return sortByRiskAndDate(events.filter((event) => isActionRequired(event, today)));
};

export const getSafeguardingSummary = (events = [], leadName, today = new Date()) => {
  const overallRisk = events.reduce((current, event) => {
    const nextRisk = normalizeSeverity(event?.severity);
    return riskOrder[nextRisk] > riskOrder[current] ? nextRisk : current;
  }, 'low');
  const activeCount = events.filter((event) => isActiveStatus(event.status)).length;
  const overdueCount = events.filter((event) => isOverdueReview(event, today)).length;
  return {
    overallRisk,
    activeCount,
    overdueCount,
    leadName: leadName || '',
    leadAssigned: Boolean(leadName)
  };
};

export const riskSortOrder = riskOrder;
